/**
 * VA-27852 Phase 2 — the frontend Silero VAD streaming layer.
 *
 * WHAT THIS IS. A stateful adapter that turns an arbitrary stream of 16 kHz mono
 * Float32 microphone samples into one Silero speech probability per completed
 * 512-sample (32 ms) window, reproducing the backend's `SileroVadStream`
 * (`interview/cost_optimized/silero_vad.py`) exactly.
 *
 * WHAT THIS IS NOT. It holds no threshold, no onset timing, no cooldown, no
 * aggregation and no notion of "barge-in". It answers exactly one question —
 * "what speech probability did Silero produce for this 32 ms window?" — and
 * nothing more. Thresholding and onset semantics belong to the barge-in
 * detector; wiring belongs to the integration phase. There is deliberately NO
 * RMS path here, in either direction: not as a pre-gate, and above all not as a
 * fallback. Live shadow evidence showed the provisional RMS detector confirming
 * on silence, so falling back to it would be choosing a known-broken signal over
 * none. When this stream cannot run, it goes quiet and stays quiet.
 *
 * WHY IT TAKES A RUNTIME INSTEAD OF IMPORTING ORT. `SileroRuntime` is the whole
 * of onnxruntime-web that this module needs — three methods, mirroring
 * `new ort.Tensor(...)`, `session.run(...)` and `session.release()`. Injecting it
 * keeps this file free of ORT, of the DOM, and of React, so the tensor/state/
 * context arithmetic that actually matters can be tested directly rather than
 * through a browser. The adapter that supplies the real ORT session (self-hosted
 * WASM, numThreads=1, proxy=true) is a later phase and is a few lines long.
 *
 * THE ORDERING RULE THAT MAKES IT CORRECT. The feasibility spike found that ORT
 * transfers (detaches) input buffers when proxying: a port that reads its input
 * back after `run()`, or that keeps the returned state tensor's array, fails on
 * the SECOND inference. So this module never lets a buffer it still needs cross a
 * `run()` boundary — see `runWindow`.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { levelDbfs } from './barge-in-config';
/** 16 kHz. The interview wire rate; no resampling happens in here. */
export const SAMPLE_RATE = 16000;
/** New samples consumed per inference. Fixed by the model. */
export const WINDOW_SAMPLES = 512;
/**
 * Samples carried from the tail of the previous model input. MANDATORY: feeding a
 * bare 512-sample window makes the model return ~0 for everything, including real
 * speech (recorded in the backend module against the reference implementation).
 */
export const CONTEXT_SAMPLES = 64;
/** `[context(64), window(512)]`. */
export const INPUT_SAMPLES = CONTEXT_SAMPLES + WINDOW_SAMPLES;
/** LSTM hidden state for the op15 16 kHz model. */
export const STATE_DIMS = [2, 1, 128];
export const STATE_ELEMENTS = 2 * 1 * 128;
export const WINDOW_MS = (WINDOW_SAMPLES / SAMPLE_RATE) * 1000;
/**
 * Bounded input buffer: four windows, ~128 ms.
 *
 * Sized for correctness, not memory. Warmed inference is ~1 ms against a 32 ms
 * window, roughly 30x headroom, so a backlog past four windows is not a busy
 * moment — it is a stall. And a stall cannot be papered over by dropping the
 * oldest samples: `state` and `context` describe the audio actually fed, so
 * skipping samples leaves the model evaluating new audio against a context from
 * before the gap. That is not "slightly stale" output, it is unreliable output,
 * and it can be unreliable in the direction of a false confirmation. Overflow
 * therefore drops everything and resets (see `push`).
 */
export const MAX_BUFFERED_SAMPLES = 4 * WINDOW_SAMPLES;
/** Consecutive inference failures tolerated before the stream disables itself. */
export const MAX_CONSECUTIVE_FAILURES = 3;
/**
 * Reproduces the wire's float -> int16 -> /32768 round trip.
 *
 * The backend never sees raw floats: the microphone path converts to PCM16 before
 * the websocket, and `silero_vad.py` divides by 32768 on the way back. Skipping
 * that here would feed the browser model a signal the backend model never
 * receives, and the difference lives exactly at low amplitudes — where the
 * marginal speech/no-speech decisions are. The arithmetic below is copied from
 * `float32ToPcm16ArrayBuffer` (realtime/audio-utils.ts) deliberately, including
 * the asymmetric 0x8000/0x7fff scaling, so the two agree bit for bit.
 */
function quantizeToWireLevels(source, target, offset) {
    const pcm = new Int16Array(1);
    for (let i = 0; i < source.length; i += 1) {
        const s = Math.max(-1, Math.min(1, source[i]));
        pcm[0] = s < 0 ? s * 0x8000 : s * 0x7fff;
        target[offset + i] = pcm[0] / 32768;
    }
}
export class SileroVadStream {
    constructor(runtime, options = {}) {
        var _a, _b;
        /** LSTM state. Owned outright; never handed to ORT without copying first. */
        this.state = new Float32Array(STATE_ELEMENTS);
        /** Tail of the previous model input. Owned outright. */
        this.context = new Float32Array(CONTEXT_SAMPLES);
        this.buffered = 0;
        this.statusValue = 'ready';
        this.generationValue = 0;
        this.windowIndex = 0;
        this.consecutiveFailures = 0;
        this.overflows = 0;
        this.pumping = false;
        this.inFlight = false;
        this.settledWaiters = [];
        this.runtime = runtime;
        this.onWindow = options.onWindow;
        this.onNotice = options.onNotice;
        this.maxBuffered = (_a = options.maxBufferedSamples) !== null && _a !== void 0 ? _a : MAX_BUFFERED_SAMPLES;
        this.maxFailures = (_b = options.maxConsecutiveFailures) !== null && _b !== void 0 ? _b : MAX_CONSECUTIVE_FAILURES;
        this.buffer = new Float32Array(this.maxBuffered);
    }
    get status() {
        return this.statusValue;
    }
    get generation() {
        return this.generationValue;
    }
    snapshot() {
        return {
            status: this.statusValue,
            generation: this.generationValue,
            buffered: this.buffered,
            windows: this.windowIndex,
            consecutiveFailures: this.consecutiveFailures,
            overflows: this.overflows,
            inFlight: this.inFlight,
        };
    }
    /**
     * Accept samples of any length. Synchronous and cheap: it quantises into the
     * bounded buffer and kicks the pump. Probabilities arrive later via `onWindow`,
     * because ORT inference is asynchronous — there is no synchronous answer to
     * hand back, and pretending otherwise is how the caller ends up racing.
     */
    push(samples) {
        if (this.statusValue !== 'ready' || samples.length === 0)
            return;
        if (this.buffered + samples.length > this.maxBuffered) {
            // Overflow. Everything goes, including the model's state and context: what
            // makes a gap dangerous is not the lost audio, it is that `state` would
            // still describe audio the model no longer has. Resetting is the only way
            // back to a defined starting point. The utterance in progress loses its
            // evidence and no confirmation can come from it — which is the correct,
            // fail-closed outcome.
            const dropped = this.buffered + samples.length;
            this.overflows += 1;
            this.resetProgress();
            this.notify({ type: 'overflow', dropped });
            return;
        }
        quantizeToWireLevels(samples, this.buffer, this.buffered);
        this.buffered += samples.length;
        this.pump();
    }
    /**
     * Drop all progress and start again from the model's initial condition: zero
     * state, zero context, empty remainder. Any inference still awaiting ORT is
     * invalidated by the generation bump — its result cannot write state, context
     * or a probability into the stream it comes back to.
     *
     * Cumulative counters (`windows`, `overflows`) deliberately survive; they are
     * observability, not stream state.
     */
    reset() {
        if (this.statusValue === 'released')
            return;
        this.resetProgress();
    }
    /** Resolves once no inference is in flight and no full window is waiting. */
    settled() {
        if (!this.pumping)
            return Promise.resolve();
        return new Promise((resolve) => {
            this.settledWaiters.push(resolve);
        });
    }
    /**
     * Permanent teardown. The stream accepts nothing further and no in-flight
     * result can touch it.
     */
    release() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.statusValue === 'released')
                return;
            this.statusValue = 'released';
            this.resetProgress();
            try {
                yield this.runtime.release();
            }
            catch (_a) {
                // Releasing a session that is already gone is not a failure worth
                // surfacing — the stream is unusable either way.
            }
        });
    }
    resetProgress() {
        this.generationValue += 1;
        this.state = new Float32Array(STATE_ELEMENTS);
        this.context = new Float32Array(CONTEXT_SAMPLES);
        this.buffered = 0;
    }
    notify(notice) {
        if (!this.onNotice)
            return;
        try {
            this.onNotice(notice);
        }
        catch (_a) {
            // A listener must never break the stream.
        }
    }
    disable(reason) {
        if (this.statusValue !== 'ready')
            return;
        this.statusValue = 'disabled';
        this.resetProgress();
        this.notify({ type: 'disabled', reason });
    }
    pump() {
        if (this.pumping || this.statusValue !== 'ready')
            return;
        if (this.buffered < WINDOW_SAMPLES)
            return;
        this.pumping = true;
        void this.drain();
    }
    drain() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                while (this.statusValue === 'ready' && this.buffered >= WINDOW_SAMPLES) {
                    // eslint-disable-next-line no-await-in-loop
                    yield this.runWindow();
                }
            }
            finally {
                this.pumping = false;
                const waiters = this.settledWaiters;
                this.settledWaiters = [];
                waiters.forEach((resolve) => resolve());
            }
        });
    }
    /**
     * One inference. The ordering here is the whole point of the module.
     *
     *   1. take exactly 512 samples out of the buffer;
     *   2. build a FRESH 576-sample input — never a reused scratch array, because a
     *      transferred buffer cannot be written again;
     *   3. capture the next context (the final 64 of the input) BEFORE `run()`,
     *      while the array is still ours to read;
     *   4. hand ORT a COPY of the state, so a detached or failed call can never
     *      corrupt the state we still hold;
     *   5. copy the returned state into a fresh array rather than aliasing it;
     *   6. commit context and state only after a successful, validated inference.
     *
     * A failure therefore leaves the model exactly where it was, and nothing this
     * function passes to ORT is ever read back afterwards.
     */
    runWindow() {
        return __awaiter(this, void 0, void 0, function* () {
            const generationAtDispatch = this.generationValue;
            const input = new Float32Array(INPUT_SAMPLES);
            input.set(this.context, 0);
            input.set(this.buffer.subarray(0, WINDOW_SAMPLES), CONTEXT_SAMPLES);
            // VA-28665: measured BEFORE run() — `input` is transferred to the ORT worker.
            const windowLevelDbfs = levelDbfs(input, CONTEXT_SAMPLES, WINDOW_SAMPLES);
            // Consume from the buffer up front so a stale result cannot replay a window.
            this.buffer.copyWithin(0, WINDOW_SAMPLES, this.buffered);
            this.buffered -= WINDOW_SAMPLES;
            // (3) BEFORE run(): `input` is about to be transferred to the ORT worker.
            const nextContext = input.slice(INPUT_SAMPLES - CONTEXT_SAMPLES);
            // (4) a copy, so `this.state` survives detachment and failure alike.
            const stateForRun = new Float32Array(this.state);
            let outputs;
            this.inFlight = true;
            try {
                outputs = yield this.runtime.run({
                    input: this.runtime.tensor('float32', input, [1, INPUT_SAMPLES]),
                    state: this.runtime.tensor('float32', stateForRun, STATE_DIMS),
                    sr: this.runtime.tensor('int64', new BigInt64Array([BigInt(SAMPLE_RATE)]), []),
                });
            }
            catch (error) {
                this.inFlight = false;
                if (generationAtDispatch !== this.generationValue)
                    return;
                this.recordFailure({ type: 'inference_error', error, consecutiveFailures: 0 });
                return;
            }
            this.inFlight = false;
            // A reset or release landed while ORT was working. The result describes a
            // stream that no longer exists: it must not write state, context, or a
            // probability, and it must not count as a success or a failure.
            if (generationAtDispatch !== this.generationValue || this.statusValue !== 'ready')
                return;
            const probability = readProbability(outputs);
            if (probability === null) {
                this.recordFailure({ type: 'invalid_output', reason: 'probability', consecutiveFailures: 0 });
                return;
            }
            const nextState = readState(outputs);
            if (nextState === null) {
                this.recordFailure({ type: 'invalid_output', reason: 'state', consecutiveFailures: 0 });
                return;
            }
            // (6) commit.
            this.state = nextState;
            this.context = nextContext;
            this.consecutiveFailures = 0;
            this.windowIndex += 1;
            if (this.onWindow) {
                try {
                    this.onWindow({
                        probability,
                        levelDbfs: windowLevelDbfs,
                        index: this.windowIndex,
                        generation: generationAtDispatch,
                    });
                }
                catch (_a) {
                    // A listener must never break the stream.
                }
            }
        });
    }
    recordFailure(notice) {
        this.consecutiveFailures += 1;
        // Never carry on from a half-applied inference: the state we hold may no
        // longer correspond to the audio already consumed.
        this.resetProgress();
        this.notify(Object.assign(Object.assign({}, notice), { consecutiveFailures: this.consecutiveFailures }));
        if (this.consecutiveFailures >= this.maxFailures) {
            this.disable(`${this.consecutiveFailures} consecutive inference failures`);
        }
    }
}
/**
 * Narrow validation, on purpose. This adapter serves exactly one known model, so
 * it checks the handful of things whose corruption would be silent — a missing
 * output, a NaN probability, a state of the wrong size — and does not attempt to
 * be a general ONNX validator.
 */
function readProbability(outputs) {
    const tensor = outputs === null || outputs === void 0 ? void 0 : outputs.output;
    if (!tensor)
        return null;
    const data = tensor.data;
    if (!data || typeof data.length !== 'number' || data.length < 1)
        return null;
    const value = Number(data[0]);
    // NaN and +/-Infinity must never reach the detector: a comparison against a
    // threshold would silently be false for NaN, turning model corruption into
    // "the candidate is not speaking" forever.
    if (!Number.isFinite(value))
        return null;
    return value;
}
function readState(outputs) {
    const tensor = outputs === null || outputs === void 0 ? void 0 : outputs.stateN;
    if (!tensor)
        return null;
    const data = tensor.data;
    if (!data || typeof data.length !== 'number' || data.length !== STATE_ELEMENTS)
        return null;
    if (tensor.dims && !dimsMatch(tensor.dims, STATE_DIMS))
        return null;
    // (5) COPY. Aliasing the returned tensor's array is the second half of the
    // detachment bug: ORT may reuse or neuter it once the next call starts.
    return Float32Array.from(data);
}
function dimsMatch(actual, expected) {
    if (actual.length !== expected.length)
        return false;
    return actual.every((value, index) => value === expected[index]);
}
